import type { RoomState } from "./room-connection.js";

/**
 * What to tell the people still in a room when someone has gone (#616): from the room before and
 * after a change, whoever is no longer in it and who hosts now. Every way of going counts (Leave, a
 * closed tab or a lost connection once the room stops holding the seat), so nobody is left waiting
 * without a word. `skip` is seats this page shouldn't announce: one it removed, or one that asked
 * everyone along to a new room (that has its own card). Null when there's nothing to say.
 */
export function departureNote(
  before: RoomState,
  after: RoomState,
  me: string | null,
  skip: ReadonlySet<string> = new Set(),
): string | null {
  const gone = before.members.filter(
    (m) => m.seat !== me && !skip.has(m.seat) && !after.members.some((n) => n.seat === m.seat),
  );
  const hostChanged = before.host !== after.host;
  const newHost = after.members.find((m) => m.seat === after.host);
  const hosts = !hostChanged
    ? ""
    : after.host === me
      ? "You host the room now."
      : newHost
        ? `${newHost.name} hosts the room now.`
        : "";
  if (gone.length > 0)
    return `${names(gone.map((m) => m.name))} left the room.${hosts ? ` ${hosts}` : ""}`;
  if (!hostChanged || !newHost) return null;
  // The room's creator came back, and hosts it again.
  if (!before.members.some((m) => m.seat === newHost.seat))
    return newHost.seat === me ? null : `${newHost.name} is back, and hosts the room again.`;
  // The host's seat is still held (away), but the room has stopped waiting for them.
  const oldHost = after.members.find((m) => m.seat === before.host);
  if (oldHost && !oldHost.connected && oldHost.seat !== me && !skip.has(oldHost.seat))
    return `${oldHost.name} hasn't come back. ${hosts}`;
  return null;
}

const names = (all: string[]) =>
  all.length <= 1 ? (all[0] ?? "") : `${all.slice(0, -1).join(", ")} and ${all.at(-1) ?? ""}`;
